import { Controller } from "@hotwired/stimulus"

// CONEK — Shared mobile infinite-scroll loader.
//
// Attached to the lazy `<turbo-frame>` that holds the NEXT page (rendered by
// `Ui::Mobile::InfiniteScrollComponent`). The frame starts with no `src`, so
// it does not fetch until this controller decides to. An IntersectionObserver
// watches the sentinel inside the frame; when it scrolls within
// `rootMargin` of the viewport, the controller sets `src` on the frame, which
// makes Turbo fetch the next page and swap in rows + a fresh sentinel frame.
// Each page is its own frame instance, so each gets its own controller — when
// a page is consumed the old controller `disconnect()`s and the new one takes
// over. No manual fetch(), no scroll listeners, no @rails/request.js needed:
// Turbo Frame navigation owns the request (CSRF, credentials, headers).
//
// Registered as "ui--mobile--infinite-scroll".
export default class extends Controller {
  static targets = ["sentinel"]
  static values = {
    url: String,
    rootMargin: { type: String, default: "300px" },
  }

  connect() {
    if (!this.hasUrlValue || this.urlValue.length === 0) return
    if (!this.hasSentinelTarget) return

    // `IntersectionObserver` may be missing in very old WebViews — fall back to
    // loading immediately so the list still works (degrades to eager paging).
    if (typeof IntersectionObserver === "undefined") {
      this.#load()
      return
    }

    this.observer = new IntersectionObserver(
      (entries) => this.#onIntersect(entries),
      { rootMargin: this.rootMarginValue, threshold: 0 },
    )
    this.observer.observe(this.sentinelTarget)

    // Scroll/resize fallback. IntersectionObserver is the primary (efficient)
    // trigger, but its callback is unreliable after a *programmatic* scroll
    // (headless browsers in system tests, some WebViews) — the synthetic scroll
    // doesn't always deliver an intersection update. A passive scroll/resize
    // listener re-checks the sentinel's rect directly, so any scroll bringing it
    // within `rootMargin` of the viewport loads the next page deterministically.
    // Observer + listeners are all torn down on the first load.
    this.onScrollOrResize = () => this.#checkVisible()
    window.addEventListener("scroll", this.onScrollOrResize, { passive: true })
    window.addEventListener("resize", this.onScrollOrResize, { passive: true })
  }

  disconnect() {
    this.#teardown()
    // Reset so a re-connect (Turbo morph / back-forward cache reusing THIS
    // instance) can load again. Deliberately NOT in #teardown — #load() calls
    // that right after setting loaded=true, and resetting there would defeat the
    // double-fire guard.
    this.loaded = false
  }

  #onIntersect(entries) {
    const visible = entries.some((entry) => entry.isIntersecting)
    if (visible) this.#load()
  }

  // Direct rect check used by the scroll/resize fallback: is the sentinel within
  // `rootMargin` px of the viewport bottom (or already on screen)?
  #checkVisible() {
    if (this.loaded || !this.hasSentinelTarget) return
    const rect = this.sentinelTarget.getBoundingClientRect()
    const viewportH =
      window.innerHeight || document.documentElement.clientHeight
    const margin = parseInt(this.rootMarginValue, 10) || 0
    if (rect.top <= viewportH + margin) this.#load()
  }

  #teardown() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
    if (this.onScrollOrResize) {
      window.removeEventListener("scroll", this.onScrollOrResize)
      window.removeEventListener("resize", this.onScrollOrResize)
      this.onScrollOrResize = null
    }
  }

  #load() {
    if (this.loaded) return
    this.loaded = true

    // Stop observing/listening immediately so we never double-fire while Turbo fetches.
    this.#teardown()

    // Setting `src` on the turbo-frame triggers Turbo to fetch + swap.
    // Guard against a frame that already has a matching src (re-connect).
    if (this.element.getAttribute("src") !== this.urlValue) {
      this.element.setAttribute("src", this.urlValue)
    }
  }
}
